var _excluded = ["inputRef", "leadingZero", "max", "min", "name", "onChange", "placeholder", "required", "reverse", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { motion, useAnimation } from 'framer-motion';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '../Autocomplete';
import Select from '../Select';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      '& input': {
        padding: '0 !important'
      },
      '& .MuiInputBase-root': {
        color: function color(_ref) {
          var error = _ref.error;
          return error ? theme.palette.error.main : null;
        }
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
        borderColor: function borderColor(_ref2) {
          var error = _ref2.error;
          return error ? theme.palette.error.main : null;
        }
      },
      '& .MuiTouchRipple-root': {
        display: 'none'
      }
    },
    input: {
      margin: 0,
      '& .MuiInputBase-root': {
        padding: function padding(_ref3) {
          var size = _ref3.size;
          return size === 'small' ? theme.spacing(1, 8, 1, 2) + " !important" : theme.spacing(3, 8, 3, 4) + " !important";
        },
        border: function border(_ref4) {
          var variant = _ref4.variant;
          return variant === 'filled' ? "2px solid transparent" : null;
        },
        borderRadius: function borderRadius(_ref5) {
          var edges = _ref5.edges;
          switch (edges) {
            case 'rounded':
              return 99;
            case 'sharp':
              return 0;
            default:
              return theme.shape.borderRadius;
          }
        }
      },
      '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: function borderColor(_ref6) {
          var required = _ref6.required,
            showError = _ref6.showError,
            value = _ref6.value;
          return required && !value || showError ? theme.palette.secondary.main : null;
        }
      },
      '& .MuiFilledInput-root': {
        backgroundColor: theme.palette.grey.transparent
      },
      '& .Mui-focused': {
        border: function border(_ref7) {
          var variant = _ref7.variant,
            required = _ref7.required,
            value = _ref7.value;
          return variant === 'filled' ? "2px solid " + (required && !value ? theme.palette.secondary.main : theme.palette.primary.main) : null;
        }
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: function borderColor(_ref8) {
          var required = _ref8.required,
            value = _ref8.value;
          return required && !value ? theme.palette.secondary.main : null;
        }
      },
      '& input': {
        padding: '0 !important'
      }
    }
  };
});
var DateNumberInput = function DateNumberInput(_ref9) {
  var inputRef = _ref9.inputRef,
    leadingZero = _ref9.leadingZero,
    max = _ref9.max,
    min = _ref9.min,
    name = _ref9.name,
    onChange = _ref9.onChange,
    placeholder = _ref9.placeholder,
    required = _ref9.required,
    reverse = _ref9.reverse,
    value = _ref9.value,
    rest = _objectWithoutPropertiesLoose(_ref9, _excluded);
  var _useState = useState(false),
    error = _useState[0],
    setError = _useState[1];
  var _useState2 = useState(null),
    selected = _useState2[0],
    setSelected = _useState2[1];
  var classes = useStyles({
    error: error,
    required: required,
    value: value
  });
  var controls = useAnimation();
  var maxLength = max.toString().length;
  var isNotMobile = useMediaQuery(function (theme) {
    return theme.breakpoints.up('sm');
  });
  useEffect(function () {
    if (error) {
      controls.start({
        translateX: ['-12px', '10px', '-6px', '4px'],
        transition: {
          duration: 0.25,
          ease: 'easeInOut'
        }
      });
      inputRef.current.focus();
      if (isNotMobile) {
        inputRef.current.select();
      }
      setTimeout(function () {
        return setError(false);
      }, 500);
    }
  }, [error, controls, inputRef, isNotMobile]);
  useEffect(function () {
    if (value && parseInt(value, 10) > max) {
      onChange(max);
      setError(true);
    }
  }, [value, max, onChange]);
  var handleKeyDown = function handleKeyDown(e) {
    // prevent none [0-9] characters that are allowed by number inputs
    var notAllowed = ['-', '+', '.', 'e', 'E'];
    if (notAllowed.includes(e.key)) {
      e.preventDefault();
    }
  };
  var handleChange = function handleChange(newValue) {
    var hasError = false;
    var updatedValue = newValue;
    // strip leading zeroes...
    updatedValue = parseInt(updatedValue, 10).toString();

    // prevent user entering more characters that allowed
    if (updatedValue.length > maxLength) {
      updatedValue = updatedValue.substring(0, maxLength);
      hasError = true;
    }

    // prevent the user from entering a number larger than the max
    if (parseInt(updatedValue, 10) > max) {
      updatedValue = max;
      hasError = true;
    }
    if (hasError) {
      setError(true);
    }
    if (leadingZero && updatedValue !== '0') {
      while (updatedValue.length < maxLength) {
        updatedValue = "0" + updatedValue;
      }
    }
    onChange(updatedValue);
  };
  var handleAutocompleteChange = function handleAutocompleteChange(event, option) {
    setSelected(option);
    handleChange(option.value);
  };
  var handleNativeChange = function handleNativeChange(_ref10) {
    var target = _ref10.target;
    onChange(target.value);
  };
  var options = useMemo(function () {
    var optionArray = [];
    var count = min;
    while (count < max + 1) {
      var label = count.toString();
      if (leadingZero) {
        while (label.length < maxLength) {
          label = "0" + label;
        }
      }
      optionArray.push({
        label: label,
        value: isNotMobile ? count : parseInt(label, 10)
      });
      count += 1;
    }
    return reverse ? optionArray.reverse() : optionArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, leadingZero, maxLength]);
  var autoCompleteDefault = Object.values(options).filter(function (option) {
    return option.value === value;
  })[0];
  return /*#__PURE__*/React.createElement(motion.div, {
    animate: controls,
    className: classes.root
  }, isNotMobile ? /*#__PURE__*/React.createElement(Autocomplete, _extends({
    fullWidth: true,
    autoHighlight: true,
    autoSelect: true,
    blurOnSelect: true,
    value: selected || autoCompleteDefault || null,
    noOptionsText: "Invalid",
    options: [{
      value: '',
      label: ''
    }].concat(options),
    disableClearable: true,
    name: name,
    required: required,
    onChange: handleAutocompleteChange,
    getOptionLabel: function getOptionLabel(option) {
      return option.label ? option.label : '';
    },
    renderInput: function renderInput(params) {
      return /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
        value: value,
        className: classes.input,
        placeholder: placeholder,
        name: name,
        variant: "outlined",
        onChange: handleNativeChange,
        onKeyDown: handleKeyDown,
        inputRef: inputRef,
        required: required,
        inputProps: _extends({
          autoComplete: 'off'
        }, params.inputProps)
      }));
    }
  }, rest)) : /*#__PURE__*/React.createElement(Select, _extends({
    fullWidth: true,
    value: !isNaN(value) ? value : null,
    required: required,
    placeholder: placeholder,
    onChange: handleNativeChange,
    options: [{
      value: '',
      label: placeholder
    }].concat(options),
    inputRef: inputRef,
    name: name,
    native: true
  }, rest)));
};
DateNumberInput.propTypes = process.env.NODE_ENV !== "production" ? {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]).isRequired,
  leadingZero: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  reverse: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
} : {};
DateNumberInput.defaultProps = {
  leadingZero: true,
  min: 0,
  placeholder: '',
  required: false,
  reverse: false,
  value: ''
};
export default DateNumberInput;